'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import MainContainer from 'layout/src/components/MainContainer';
import IconComponent from 'ui/src/components/Icon';
import useWindowSize from 'ui/src/hooks/useWindowSize';
import twMerge from 'utilities/src/twMerge';
import { getCategoryPath, getSubcategoryPath } from 'data/taxonomy';
import type { NavbarCategory } from './types';

interface Props {
  ref?: React.Ref<HTMLDivElement>;
  id: string;
  isOpen: boolean;
  categories: NavbarCategory[];
  activeCategorySlug?: string | null;
  search?: React.ReactNode;
  onNavigate: () => void;
}

/**
 * One list of links restyled per breakpoint, so every category and subcategory link is in the HTML once:
 * a five-column grid from 1024px, a two-pane picker at 834px and an accordion below it.
 */
export default function CategoriesMenu({
  ref,
  id,
  isOpen,
  categories,
  activeCategorySlug,
  search,
  onNavigate,
}: Props) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const activeIndex = categories.findIndex(({ slug }) => slug === activeCategorySlug);
  const selectedIndex = openIndex ?? Math.max(activeIndex, 0);
  // The default viewports' tablet range (834–1023px) is exactly the two-pane picker.
  const { isTablet: isTwoPane } = useWindowSize();

  // The two-pane picker always shows one category; the accordion can close the open one.
  const onCategoryClick = (index: number) => setOpenIndex(isTwoPane || index !== openIndex ? index : null);

  return (
    <div
      ref={ref}
      id={id}
      className={twMerge(
        'bg-site-neutrals-0 mdS:absolute mdS:inset-x-0 mdS:top-full shadow-lv5 fixed inset-x-0 top-20 max-h-[calc(100dvh-5rem)] overflow-y-auto',
        'mdS:max-h-none mdS:overflow-visible transition-all duration-200',
        isOpen ? 'visible translate-y-0 opacity-100' : 'pointer-events-none invisible -translate-y-2 opacity-0',
      )}
    >
      <MainContainer className="mdS:pt-10 mdL:pt-10 flex flex-col gap-6 pb-14 pt-4 md:pt-6">
        {search}
        <ul className="mdL:grid-cols-5 mdL:gap-y-6 mdS:gap-7 flex flex-col gap-6 md:grid md:grid-cols-[21.875rem_minmax(0,1fr)] md:gap-x-6 md:gap-y-0">
          {categories.map((category, index) => {
            const number = String(index + 1).padStart(2, '0');
            const listId = `${id}-${category.slug}`;
            const isSelected = index === selectedIndex;
            const isExpanded = index === openIndex;

            return (
              <li key={category.slug} className="mdL:flex flex flex-col gap-2 md:contents">
                <div className="border-site-neutrals-100 mdL:flex hidden flex-col gap-0.5 border-b pb-2.5">
                  <span aria-hidden="true" className="site-overline-s text-site-primary-600">
                    {number}
                  </span>
                  <Link
                    href={getCategoryPath(category.slug)}
                    prefetch={false}
                    onClick={onNavigate}
                    className="site-paragraph-m text-site-neutrals-900 font-bold hover:underline"
                  >
                    {category.name}
                  </Link>
                  {category.count > 0 && (
                    <span className="site-paragraph-xs text-site-neutrals-400 font-medium">
                      {category.count} articles
                    </span>
                  )}
                </div>

                <button
                  type="button"
                  aria-expanded={isTwoPane ? isSelected : isExpanded}
                  aria-controls={listId}
                  onClick={() => onCategoryClick(index)}
                  className={twMerge(
                    'border-site-neutrals-100 text-site-neutrals-900 mdL:hidden flex items-center gap-2 border-b pb-4 text-left',
                    'md:hover:text-site-neutrals-900 md:col-start-1 md:border-b-0 md:border-l md:border-r md:px-2.5 md:py-3',
                    isSelected
                      ? 'md:bg-site-neutrals-25 md:border-l-site-primary-600 md:text-site-neutrals-900'
                      : 'md:text-site-neutrals-600 md:border-l-transparent',
                  )}
                >
                  <span className="flex min-w-0 flex-1 items-center gap-2 md:gap-2.5">
                    <span
                      aria-hidden="true"
                      className="site-overline-m md:site-overline-s text-site-primary-600 w-[1.375rem] shrink-0"
                    >
                      {number}
                    </span>
                    <span className={twMerge('site-paragraph-m font-bold', { 'md:font-normal': !isSelected })}>
                      {category.name}
                    </span>
                  </span>
                  <span className="text-site-neutrals-600 md:hidden">
                    <IconComponent
                      iconName="ExpandMore"
                      className={twMerge('bg-current transition-transform duration-200', {
                        'rotate-180': isExpanded,
                      })}
                    />
                  </span>
                  <span className="hidden md:block">
                    <IconComponent iconName="ChevronRightSmall" className="bg-current" />
                  </span>
                </button>

                <div
                  id={listId}
                  className={twMerge(
                    'hidden flex-col',
                    { flex: isExpanded },
                    isSelected ? 'md:flex' : 'md:hidden',
                    'mdL:flex md:col-start-2 md:row-span-10 md:row-start-1 md:gap-4',
                  )}
                >
                  <p className="site-display-s text-site-neutrals-900 mdL:hidden hidden font-medium md:block">
                    {category.name}
                  </p>
                  <div className="bg-site-neutrals-200 mdL:hidden hidden h-px md:block" />
                  <ul className="md:divide-site-neutrals-50 mdL:divide-y-0 mdL:pl-0 mdL:gap-2 flex flex-col gap-2 pl-[1.875rem] md:gap-0 md:divide-y md:pl-0">
                    {category.subcategories.map((subcategory) => (
                      <li key={subcategory.slug} className="mdL:py-0 md:py-2.5 md:first:pt-0">
                        <Link
                          href={getSubcategoryPath(category.slug, subcategory.slug)}
                          prefetch={false}
                          onClick={onNavigate}
                          className="group flex items-center gap-[0.3125rem]"
                        >
                          <span className="site-paragraph-s text-site-neutrals-600 md:site-paragraph-m md:text-site-neutrals-700 mdL:site-paragraph-s mdL:text-site-neutrals-600 flex-1 group-hover:underline">
                            {subcategory.name}
                          </span>
                          {subcategory.count > 0 && (
                            <span className="site-paragraph-xs bg-site-neutrals-25 text-site-neutrals-400 group-hover:bg-site-neutrals-50 group-hover:text-site-neutrals-600 mdL:hidden hidden min-w-8 rounded-lg px-1 py-0.5 text-center md:block">
                              {subcategory.count}
                            </span>
                          )}
                        </Link>
                      </li>
                    ))}
                    <li className="mdL:hidden md:py-2.5">
                      <Link
                        href={getCategoryPath(category.slug)}
                        prefetch={false}
                        onClick={onNavigate}
                        className="group flex items-center gap-[0.3125rem]"
                      >
                        <span className="site-paragraph-s text-site-neutrals-600 md:site-paragraph-m md:text-site-neutrals-700 flex-1 group-hover:underline">
                          <span className="md:hidden">All {category.name}</span>
                          <span className="hidden md:inline">All Articles</span>
                        </span>
                        {category.count > 0 && (
                          <span className="site-paragraph-xs bg-site-neutrals-25 text-site-neutrals-400 group-hover:bg-site-neutrals-50 group-hover:text-site-neutrals-600 hidden min-w-8 rounded-lg px-1 py-0.5 text-center md:block">
                            {category.count}
                          </span>
                        )}
                      </Link>
                    </li>
                  </ul>
                </div>
              </li>
            );
          })}
        </ul>
      </MainContainer>
    </div>
  );
}
